import React from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  getExpertiseCategories,
  getSubcategoriesByCategorySlug,
} from "~/lib/functions/expertise";
import { Heading } from "~/components/ui/heading";
import { Card, CardHeader, CardTitle } from "~/components/ui/card";

export const Route = createFileRoute("/_public/coaches/subcategory/")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["subcategories-grouped"],
      queryFn: async () => {
        const categories = await getExpertiseCategories();
        const grouped = await Promise.all(
          categories.map(async (cat) => {
            const subcategories = await getSubcategoriesByCategorySlug({
              data: { categorySlug: cat.slug },
            });
            return { category: cat, subcategories };
          }),
        );
        return grouped;
      },
    });
    return {};
  },
  head: () => ({
    meta: [
      {
        title: "Coach Subcategories | Dream Coach Match",
      },
    ],
  }),
  component: SubcategoriesPage,
});

function SubcategoriesPage() {
  const { data: grouped } = useQuery({
    queryKey: ["subcategories-grouped"],
    queryFn: async () => {
      const categories = await getExpertiseCategories();
      const grouped = await Promise.all(
        categories.map(async (cat) => {
          const subcategories = await getSubcategoriesByCategorySlug({
            data: { categorySlug: cat.slug },
          });
          return { category: cat, subcategories };
        }),
      );
      return grouped;
    },
  });

  if (!grouped) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Heading
        preHeadline="Dream Coach Match"
        headline="Find coaches by subcategory"
        subheadline="Explore coaching subcategories to find your perfect match"
        isH1
      />
      <div className="mt-8 space-y-12">
        {grouped.map(({ category, subcategories }) => (
          <div key={category.id}>
            <h2 className="mb-4 text-2xl font-semibold text-gray-800">
              {category.name}
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {subcategories.map((sub) => (
                <Card key={sub.id} className="hover:shadow-md">
                  <CardHeader>
                    <CardTitle>
                      <Link
                        to="/coaches/subcategory/$subcategorySlug"
                        params={{ subcategorySlug: sub.slug }}
                        className="block text-lg font-medium text-gray-900"
                      >
                        {sub.name}
                      </Link>
                    </CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
